import FilterConstants from '../../filter-constants';
import ToleranceInput from '../inputs/tolerance';
import C1 from '../components/c1';
import OpAmpSelection from '../component-selector/op-amp-selection';
import MathUtils from './math';
import * as CommonUtils from '../../utils/common';

export function computeR3AndR4(r3plusr4, gain) {
  var r3, r4;
  var result;

  if (gain >= 1.0001) {
    r3 = r3plusr4 / gain;
    r4 = r3plusr4 - r3;
    //make sure r3 value stays reasonable (at least 1 ohm)
    if (r3 < FilterConstants.Limits.RSMALL_COMP_LIMIT) {
      r4 = (r4 * FilterConstants.Limits.RSMALL_COMP_LIMIT) / r3;
      r3 = FilterConstants.Limits.RSMALL_COMP_LIMIT;
    }
  } else if (gain < 1.0001 && gain > 0.9999) {
    r3 = FilterConstants.Limits.INFINITY; //case of no resistor
    r4 = 0;
  } else {
    //error gain should never be less than 1
    return;
  }

  result = { r3: r3, r4: r4 };

  return result;
}

export function computeMasterCapacitorValue(optimization, stageFp) {
  var initialCapacitorValue = optimization.determineCapacitorValue(stageFp);
  var toleranceInput = new ToleranceInput({
    capacitorTolerance: FilterConstants.capacitorTolerance.ideal,
    capacitorPreferredSeries: 'E24',
  });

  var c1 = C1.create({ idealValue: initialCapacitorValue });
  var result = c1.toCommercialComponent(toleranceInput).getNominalValue();
  return result;
}

export function getInverseOfMasterCapacitorRatio(c1) {
  var c1Value = c1.value;
  var c1Tiny = c1.minimum;
  var c1Big = c1.maximum;
  var result = 1 - MathUtils.logBaseTen(c1Value / c1Tiny) / MathUtils.logBaseTen(c1Big / c1Tiny);
  return result;
}

export function computeGbwToleranceArray(opAmp, toleranceInput) {
  var result = [];
  var gbw = opAmp.gbw;
  var opAmpGbwTolerance = toleranceInput.opAmpGbwTolerance;

  if (opAmpGbwTolerance === FilterConstants.gbwTolerance.twenty) {
    result.push(gbw * 0.8, gbw * 1, gbw * 1.2);
  } else {
    result.push(gbw);
  }

  return result;
}

export function copyCircuit(circuit, isCommercial, toleranceInput) {
  var circuitModel = circuit.circuitModel;
  var algorithm = circuit.algorithm;
  var stageFp = circuit.stageFp;
  var gain = circuit.gain;
  var stageQ = circuit.stageQ;
  var optimization = circuit.optimization;
  var opAmpData = circuit.opAmp.data;
  var initialCapacitorValue = circuit.masterCapacitor.value;
  var r3Value = circuit.r3 ? circuit.r3.value : null;
  var gbw = circuit.opAmp.gbw;
  var opAmpCopy = null;
  if (opAmpData !== null) {
    opAmpCopy = OpAmpSelection.create({ data: opAmpData, gbw: gbw });
  } else {
    var rOut = circuit.opAmp.rOut;
    var specialGain = circuit.opAmp.specialGain;
    var iBias = circuit.opAmp.iBias;
    opAmpCopy = OpAmpSelection.create({ gbw: gbw, rOut: rOut, specialGain: specialGain, iBias: iBias });
  }

  var hash = {
    algorithm: algorithm,
    stageFp: stageFp,
    gain: gain,
    stageQ: stageQ,
    optimization: optimization,
    opAmp: opAmpCopy,
    initialCapacitorValue: initialCapacitorValue,
  };
  var result = circuitModel.create(hash);
  result.changeGainValues(r3Value);

  if ((typeof isCommercial === 'undefined' && toleranceInput !== null) || isCommercial) {
    toCommercialCircuit(result, toleranceInput);
  }

  return result;
}

export function toCommercialCircuit(circuit, toleranceInput) {
  if (toleranceInput === null) {
    throw 'toleranceInput should not be null';
  }

  var supportedComponents = circuit.supportedComponents;
  for (let i = 0; i < circuit.supportedComponents.length; i++) {
    if (supportedComponents[i] === 'r4') circuit.updateR4();

    circuit.get(supportedComponents[i]).toCommercialComponent(toleranceInput);
  }

  return circuit;
}

export function getResistorSize(resistor) {
  var idealValue = resistor.idealValue;
  var minimum = resistor.minimum;
  var maximum = resistor.maximum;
  var result = null;

  if (idealValue < minimum) {
    result = FilterConstants.sizing.tiny;
  } else if (idealValue > maximum) {
    result = FilterConstants.sizing.big;
  }

  return result;
}

export function getGainChangeSupportStatus(r3Value, r4Value) {
  var disallow =
    //If R3 = 0, then it's not being used at all
    r3Value === 0 ||
    //If R3 is infinity, this means we have a buffer configuration
    CommonUtils.isNearInfinity(r3Value) ||
    //If R4 is huge compared to R3, then we don't know how to calculate slider, so don't allow (this should never happen)
    r4Value / r3Value >= FilterConstants.Limits.RBIG_COMP_LIMIT / FilterConstants.Limits.RSMALL_COMP_LIMIT;

  return !disallow;
}

export function getResistorMinimumLimitMultiplier(r3Value, r4Value) {
  var result = FilterConstants.Limits.RSMALL_LOAD_LIMIT / (r3Value + r4Value);
  var r3TinyTemp = r3Value * result;

  if (r3TinyTemp < FilterConstants.Limits.RSMALL_COMP_LIMIT) {
    result = (result * FilterConstants.Limits.RSMALL_COMP_LIMIT) / r3TinyTemp;
  }

  return result;
}

export function getResistorMaximumLimitMultiplier(r3Value, r4Value) {
  var result = FilterConstants.Limits.RBIG_COMP_LIMIT / r3Value;
  var rBigTemp = r4Value * result;

  if (rBigTemp > FilterConstants.Limits.RBIG_COMP_LIMIT) {
    result = (result * FilterConstants.Limits.RBIG_COMP_LIMIT) / rBigTemp;
  }

  return result;
}

export function computeCapacitorLimit(capacitorBaseValue, capacitorLimitMultiplier) {
  return capacitorBaseValue * capacitorLimitMultiplier;
}

export function computeGainResistorLimit(resistorBaseValue, resistorLimitMultiplier) {
  // Gain Resistors are R3 and R4
  return resistorBaseValue * resistorLimitMultiplier;
}

export function computeResistorLimit(resistorBaseValue, capacitorLimitMultiplier) {
  return resistorBaseValue / capacitorLimitMultiplier;
}

export function computeComponentValueFromMinMaxAndRcRatio(minimum, maximum, rcRatio) {
  var result = minimum * Math.pow(10, MathUtils.logBaseTen(maximum / minimum) * rcRatio);
  return result;
}

export function createToleranceInputDefaults() {
  return new ToleranceInput({
    capacitorTolerance: FilterConstants.capacitorTolerance.five,
    resistorTolerance: FilterConstants.resistorTolerance.one,
    inductorTolerance: FilterConstants.inductorTolerance.five,
    opAmpGbwTolerance: FilterConstants.gbwTolerance.twenty,
    capacitorPreferredSeries: FilterConstants.eSeries.E24,
    resistorPreferredSeries: FilterConstants.eSeries.E96,
    inductorPreferredSeries: FilterConstants.eSeries.E12,
  });
}
